import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

export default function Templates() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Form State
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/templates');
      setTemplates(data);
    } catch (err) {
      console.error(err);
      alert('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAI = async () => {
    if (!aiPrompt) return alert('Enter a prompt for AI first');
    setGenerating(true);
    try {
      const { data } = await api.post('/api/templates/generate', { prompt: aiPrompt });
      setContent(data.content);
      if (!name) setName('AI Generated ' + new Date().toLocaleTimeString());
    } catch (err) {
      console.error(err);
      alert('AI Generation failed. Check console.');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!name || !content) return alert('Name and Content are required');
    setSaving(true);
    try {
      await api.post('/api/templates', { name, content });
      setName('');
      setContent('');
      setAiPrompt('');
      fetchTemplates();
    } catch (err) {
      alert('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await api.delete(`/api/templates/${id}`);
      fetchTemplates();
    } catch (err) {
      alert('Failed to delete template');
    }
  };

  if (loading) return <div style={{ padding: 40, color: '#f1f5f9' }}>Loading templates...</div>;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>Template Manager</h1>
        <p style={styles.subtitle}>Create and manage reusable message templates for WhatsApp Broadcasts.</p>
      </div>

      <div style={styles.formCard}>
        <h3 style={styles.sectionTitle}>Create New Template</h3>
        
        <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <label style={styles.label}>Template Name</label>
            <input 
              style={styles.input} 
              value={name} 
              onChange={(e) => setName(e.target.value)} 
              placeholder="e.g. Summer Course Promo"
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={styles.label}>Groq AI Magic (Optional)</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input 
                style={styles.input} 
                value={aiPrompt} 
                onChange={(e) => setAiPrompt(e.target.value)} 
                placeholder="Describe your message..."
              />
              <button 
                style={styles.aiBtn} 
                disabled={generating} 
                onClick={handleGenerateAI}
              >
                {generating ? 'Generating...' : 'Generate'}
              </button>
            </div>
          </div>
        </div>

        <label style={styles.label}>Message Content (Use {"{{"}name{"}}"} for personalization)</label>
        <textarea 
          style={styles.textarea} 
          rows={5}
          value={content} 
          onChange={(e) => setContent(e.target.value)} 
          placeholder="Hi {{name}}, we have a special offer..."
        />
        
        <button style={styles.saveBtn} disabled={saving} onClick={handleSave}>
          {saving ? 'Saving...' : 'Save Template'}
        </button>
      </div>

      <div style={styles.grid}>
        {templates.map(t => (
          <div key={t._id} style={styles.card}>
            <div style={styles.cardHeader}>
              <h4 style={styles.cardName}>{t.name}</h4>
              <button style={styles.delBtn} onClick={() => handleDelete(t._id)}>🗑️</button>
            </div>
            <pre style={styles.cardContent}>{t.content}</pre>
          </div>
        ))}
        {templates.length === 0 && <p style={{ color: '#64748b' }}>No templates created yet.</p>}
      </div>
    </div>
  );
}

const styles = {
  page: { padding: '28px 32px', flex: 1, overflowY: 'auto', background: 'transparent' },
  header: { marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 700, color: '#e2e8f0', marginBottom: 4, letterSpacing: -0.3 },
  subtitle: { color: '#64748b', fontSize: 13 },
  formCard: { background: 'rgba(6, 12, 28, 0.9)', border: '1px solid rgba(99,102,241,0.15)', borderRadius: 12, padding: 22, marginBottom: 32, backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' },
  sectionTitle: { fontSize: 15, fontWeight: 600, color: '#e2e8f0', marginBottom: 16 },
  label: { fontSize: 11, color: '#64748b', display: 'block', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 },
  input: { width: '100%', padding: '10px 12px', background: 'rgba(2, 5, 16, 0.85)', border: '1px solid rgba(99,102,241,0.18)', borderRadius: 8, color: '#e2e8f0', fontSize: 13, outline: 'none', fontFamily: 'inherit' },
  textarea: { width: '100%', padding: '12px', background: 'rgba(2, 5, 16, 0.85)', border: '1px solid rgba(99,102,241,0.18)', borderRadius: 8, color: '#e2e8f0', fontSize: 13, resize: 'vertical', fontFamily: 'inherit', marginBottom: 16, outline: 'none' },
  aiBtn: { padding: '0 16px', background: 'linear-gradient(135deg, #7c3aed, #a855f7)', border: 'none', borderRadius: 8, color: 'white', fontWeight: 600, cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap', fontFamily: 'inherit' },
  saveBtn: { padding: '10px 22px', background: 'linear-gradient(135deg, #6366f1, #818cf8)', border: 'none', borderRadius: 8, color: 'white', fontWeight: 600, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 },
  card: { background: 'rgba(6, 12, 28, 0.88)', border: '1px solid rgba(99,102,241,0.12)', borderRadius: 12, padding: '16px 18px', display: 'flex', flexDirection: 'column', backdropFilter: 'blur(10px)' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, borderBottom: '1px solid rgba(99,102,241,0.1)', paddingBottom: 10 },
  cardName: { fontSize: 14, fontWeight: 600, color: '#e2e8f0', margin: 0 },
  delBtn: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: 13, padding: '2px 6px', borderRadius: 4 },
  cardContent: { margin: 0, fontSize: 13, color: '#94a3b8', whiteSpace: 'pre-wrap', fontFamily: 'inherit', lineHeight: 1.55, flex: 1 }
};
